import { Component, EventEmitter, Input, OnChanges, Output } from '@angular/core';

import { ActualSelected, Utils } from '../../../../../shared/utils';
import { ConfirmService } from '../../../../shared/services/confirm.service';
import { NotificationService } from '../../../../shared/services/notification.service';
import { TierService } from '../../../../shared/services/tier.service';
import { ConfirmDialogDetail } from '../../../../shared/components/confirm-dialog.component';
import {
  conceptPrefix,
  CREDIT_SETTLEMENT_LABELS,
  CREDIT_TERM_LABELS,
  CreditDetailAction,
  CreditKind,
  CreditOperation,
  creditCollected,
  creditPercent,
  creditStatus,
  creditTitle,
  displayDate,
  roundMoney,
  sortByDateDesc,
  stripConceptPrefix,
} from '../../models/credit-shared.model';
import { CreditOperationsService } from '../../services/credit-operations.service';

interface CreditDetailLine {
  label: string;
  value: string;
}

interface CreditMovementLine {
  uid: string;
  date: string;
  concept: string;
  meta: string;
  amount: number;
}

interface CreditMovementBlock {
  operation: CreditOperation;
  title: string;
  note: string;
  icon: string;
  empty: string;
  count: number;
  total: number;
  canAdd: boolean;
  lines: CreditMovementLine[];
}

interface CreditDeleteConfig {
  title: string;
  message: string;
  loadingText: string;
  errorText: string;
  successText: string;
}

const MAX_LINES = 5;

const DELETE_CONFIG: Record<CreditOperation, CreditDeleteConfig> = {
  constitution: {
    title: 'Eliminar constitución',
    message: 'Se eliminará la constitución del crédito y su asiento contable. Si estaba conciliada, el movimiento bancario volverá a quedar pendiente.',
    loadingText: 'Eliminando constitución…',
    errorText: 'No se pudo eliminar la constitución',
    successText: 'Constitución eliminada correctamente',
  },
  shortTermDebt: {
    title: 'Eliminar deuda a corto plazo',
    message: 'Se eliminará la reclasificación a corto plazo y su asiento contable. Esta acción no se puede deshacer.',
    loadingText: 'Eliminando deuda a corto plazo…',
    errorText: 'No se pudo eliminar la deuda a corto plazo',
    successText: 'Deuda a corto plazo eliminada correctamente',
  },
  installments: {
    title: 'Eliminar cobro',
    message: 'Se eliminará el cobro y su asiento contable, y el capital volverá a sumarse al importe pendiente del crédito. Si estaba conciliado, el movimiento bancario volverá a quedar pendiente.',
    loadingText: 'Eliminando cobro…',
    errorText: 'No se pudo eliminar el cobro',
    successText: 'Cobro eliminado correctamente',
  },
};

@Component({
  selector: 'app-credit-detail-panel',
  templateUrl: './credit-detail-panel.component.html',
  styleUrls: ['./credit-detail-panel.component.scss'],
  standalone: false,
})
export class CreditDetailPanelComponent implements OnChanges {
  @Input() kind: CreditKind = 'entity';
  @Input() row: any;
  @Input() version = 0;
  @Output() action = new EventEmitter<CreditDetailAction>();

  readonly maxLines = MAX_LINES;

  loading = true;
  error = false;

  lines: CreditDetailLine[] = [];
  blocks: CreditMovementBlock[] = [];

  private loadedSignature = '';

  constructor(
    private operations: CreditOperationsService,
    private confirm: ConfirmService,
    private notify: NotificationService,
    private tier: TierService,
  ) { }

  get isEmployee(): boolean {
    return this.kind === 'employee';
  }

  get yearLabel(): string {
    return this.isEmployee ? 'Histórico' : 'Ejercicio ' + ActualSelected.yearSelected;
  }

  get initialAmount(): number {
    return roundMoney(this.row && this.row.importeInicial);
  }

  get pendingAmount(): number {
    return roundMoney(this.row && this.row.importePendiente);
  }

  get collectedAmount(): number {
    return creditCollected(this.row);
  }

  get percent(): number {
    return creditPercent(this.row);
  }

  get status(): { label: string; variant: any } {
    return creditStatus(this.row);
  }

  get canShortTermDebt(): boolean {
    return this.isEmployee || (this.row && this.row.duracion === 2);
  }

  ngOnChanges(): void {
    if (!this.row) return;
    const signature = [this.kind, this.row.uid, this.row.importeInicial, this.row.importePendiente, this.version].join('|');
    if (signature === this.loadedSignature) return;
    this.loadedSignature = signature;
    this.load(this.row.uid);
  }

  retry(): void {
    if (!this.row) return;
    this.load(this.row.uid);
  }

  editCredit(): void {
    this.action.emit({ type: 'edit' });
  }

  add(block: CreditMovementBlock): void {
    this.action.emit({ type: block.operation });
  }

  manage(block: CreditMovementBlock): void {
    this.action.emit({ type: block.operation });
  }

  editLine(block: CreditMovementBlock, line: CreditMovementLine): void {
    this.action.emit({ type: block.operation, editUid: line.uid });
  }

  deleteLine(block: CreditMovementBlock, line: CreditMovementLine): void {
    if (!this.tier.requireAdvancedTier()) return;
    const config = DELETE_CONFIG[block.operation];
    this.confirm.open({
      title: config.title,
      subtitle: creditTitle(this.kind, this.row) || undefined,
      message: config.message,
      tone: 'danger',
      details: this.details(block, line),
      confirmText: 'Eliminar',
      confirmIcon: 'delete',
      loadingText: config.loadingText,
      errorText: config.errorText,
      action: () => this.deleteRequest(block.operation, line.uid).then(ok => { if (ok !== true) throw new Error(); }),
    }).subscribe(deleted => {
      if (!deleted) return;
      this.notify.ok(config.successText);
      this.action.emit({ type: 'changed' });
    });
  }

  trackByOperation(_: number, block: CreditMovementBlock): string {
    return block.operation;
  }

  trackByUid(_: number, item: { uid: string }): string {
    return item.uid;
  }

  private deleteRequest(operation: CreditOperation, uid: string): Promise<boolean> {
    if (operation === 'constitution') return this.operations.deleteConstitution(this.kind, uid);
    if (operation === 'shortTermDebt') return this.operations.deleteShortTermDebt(this.kind, uid);
    return this.operations.deleteInstallment(this.kind, uid);
  }

  private load(uid: string): void {
    this.loading = !this.lines.length;
    this.error = false;

    Promise.all([
      this.operations.get(this.kind, uid).catch(() => null),
      this.operations.listConstitutions(this.kind, uid),
      this.operations.listShortTermDebts(this.kind, uid),
      this.operations.listInstallments(this.kind, uid),
    ])
      .then(([credit, constitutions, shortTermDebts, installments]) => {
        if (!this.row || uid !== this.row.uid) return;
        this.lines = this.buildLines({ ...this.row, ...(credit || {}) });
        this.blocks = this.buildBlocks(
          sortByDateDesc(constitutions, 'fecha'),
          sortByDateDesc(shortTermDebts, 'fecha'),
          sortByDateDesc(installments, 'fecha'),
        );
      })
      .catch(() => { this.error = true; })
      .then(() => { this.loading = false; });
  }

  private buildBlocks(constitutions: any[], shortTermDebts: any[], installments: any[]): CreditMovementBlock[] {
    const blocks: CreditMovementBlock[] = [
      {
        operation: 'constitution',
        title: 'Constitución',
        note: '',
        icon: 'account_balance_wallet',
        empty: 'Sin constituciones registradas',
        count: constitutions.length,
        total: this.sum(constitutions, 'importe'),
        canAdd: true,
        lines: constitutions.slice(0, MAX_LINES).map(item => ({
          uid: item.uid,
          date: displayDate(item.fecha),
          concept: this.concept(item),
          meta: [
            this.isEmployee ? CREDIT_TERM_LABELS[item.plazo] : '',
            Number(item.comisionApertura) ? 'Comisión ' + Utils.FormatMoney(item.comisionApertura) + ' €' : '',
            Number(item.gastosBancarios) ? 'Gastos ' + Utils.FormatMoney(item.gastosBancarios) + ' €' : '',
            item.bankAccountInfo,
          ].filter(Boolean).join(' · '),
          amount: Number(item.importe) || 0,
        })),
      },
    ];

    if (this.canShortTermDebt || shortTermDebts.length) {
      blocks.push({
        operation: 'shortTermDebt',
        title: 'Deuda a corto plazo',
        note: '',
        icon: 'swap_vert',
        empty: 'Sin reclasificaciones a corto plazo',
        count: shortTermDebts.length,
        total: this.sum(shortTermDebts, 'importe'),
        canAdd: !!this.canShortTermDebt,
        lines: shortTermDebts.slice(0, MAX_LINES).map(item => ({
          uid: item.uid,
          date: displayDate(item.fecha),
          concept: this.concept(item),
          meta: item.activitiesName || '',
          amount: Number(item.importe) || 0,
        })),
      });
    }

    blocks.push({
      operation: 'installments',
      title: 'Cuotas cobradas',
      note: this.sum(installments, 'intereses') ? 'Intereses ' + Utils.FormatMoney(this.sum(installments, 'intereses')) + ' €' : '',
      icon: 'payment',
      empty: 'Sin cuotas cobradas',
      count: installments.length,
      total: this.sum(installments, 'importeCapital'),
      canAdd: true,
      lines: installments.slice(0, MAX_LINES).map(item => ({
        uid: item.uid,
        date: displayDate(item.fecha),
        concept: this.concept(item),
        meta: [
          Number(item.intereses) ? 'Intereses ' + Utils.FormatMoney(item.intereses) + ' €' : '',
          item.bankAccountInfo,
        ].filter(Boolean).join(' · '),
        amount: Number(item.importeCapital) || 0,
      })),
    });

    return blocks;
  }

  private concept(item: any): string {
    return stripConceptPrefix(item.concepto, conceptPrefix(this.kind, this.row));
  }

  private sum(items: any[], field: string): number {
    return roundMoney(items.reduce((acc, item) => acc + (Number(item[field]) || 0), 0));
  }

  private details(block: CreditMovementBlock, line: CreditMovementLine): ConfirmDialogDetail[] {
    const details: ConfirmDialogDetail[] = [];
    if (line.date) details.push({ label: 'Fecha', value: line.date });
    details.push({ label: 'Concepto', value: line.concept || 'Sin concepto' });
    if (line.meta) details.push({ label: 'Detalle', value: line.meta });
    details.push({ label: block.operation === 'installments' ? 'Capital' : 'Importe', value: Utils.FormatMoney(line.amount) + ' €' });
    return details;
  }

  private buildLines(credit: any): CreditDetailLine[] {
    const lines: CreditDetailLine[] = [];
    if (this.isEmployee) {
      if (credit.employeesName) lines.push({ label: 'Empleado', value: credit.employeesName });
    } else {
      if (CREDIT_TERM_LABELS[credit.duracion]) lines.push({ label: 'Duración', value: CREDIT_TERM_LABELS[credit.duracion] });
      const accounts = [credit.accountingModelsCortoPlazoNumber, credit.accountingModelsLargoPlazoNumber].filter(Boolean).join(' / ');
      if (accounts) lines.push({ label: 'Cuentas CP / LP', value: accounts });
    }
    lines.push({ label: 'Constitución', value: displayDate(credit.fechaConstitucion) });
    lines.push({ label: 'Finalización', value: displayDate(credit.fechaFinalizacion) });
    if (credit.plazo != null) {
      lines.push({ label: 'Plazo', value: (Number(credit.plazo) || 0) + ' meses' + (Number(credit.carencia) ? ' · ' + credit.carencia + ' de carencia' : '') });
    }
    if (credit.tipoInteres != null) {
      lines.push({ label: 'Tipo de interés', value: (Number(credit.tipoInteres) || 0).toLocaleString('es-ES', { maximumFractionDigits: 4 }) + ' %' });
    }
    if (CREDIT_SETTLEMENT_LABELS[credit.tipoLiquidaciones]) lines.push({ label: 'Liquidaciones', value: CREDIT_SETTLEMENT_LABELS[credit.tipoLiquidaciones] });
    return lines;
  }
}
